import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, SafeAreaView, StatusBar, RefreshControl, Alert, Linking } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';
const getH = async () => { const t = await AsyncStorage.getItem('auth_token').catch(() => null); return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };

const DEFAULT_BENEFITS = [
  { id: 1, category: 'Pension', title: 'Workplace Pension', value: '5% Employee + 3% Employer', status: 'enrolled', icon: 'piggy-bank', color: '#10b981', description: 'Auto-enrolled in NEST workplace pension scheme. Contributions made monthly.' },
  { id: 2, category: 'Health', title: 'Private Medical Insurance', value: 'BUPA Cover', status: 'enrolled', icon: 'hospital-box', color: '#ef4444', description: 'Full medical cover for you and immediate family. Annual health check included.' },
  { id: 3, category: 'Life Insurance', title: 'Death in Service', value: '4x Annual Salary', status: 'active', icon: 'shield-check', color: P, description: 'Beneficiaries receive 4 times your annual salary in the event of death in service.' },
  { id: 4, category: 'Leave', title: 'Annual Leave', value: '25 days + Bank Holidays', status: 'active', icon: 'beach', color: '#f59e0b', description: 'Increasing to 30 days after 5 years of service.' },
  { id: 5, category: 'Wellness', title: 'Employee Assistance Programme', value: '24/7 Support', status: 'available', icon: 'heart-pulse', color: '#8b5cf6', description: 'Free confidential counselling, legal and financial advice available 24/7.' },
  { id: 6, category: 'Development', title: 'Learning & Development Budget', value: '£1,000/year', status: 'available', icon: 'school', color: '#06b6d4', description: 'Annual budget for training courses, conferences and professional certifications.' },
  { id: 7, category: 'Cycle', title: 'Cycle to Work Scheme', value: 'Up to £3,000', status: 'available', icon: 'bicycle', color: '#84cc16', description: 'Tax-efficient way to purchase a bicycle and cycling equipment.' },
  { id: 8, category: 'Tech', title: 'Tech Purchase Scheme', value: 'Up to £2,000', status: 'available', icon: 'laptop', color: '#f97316', description: 'Spread the cost of technology purchases over 12 months, interest-free.' },
];

const STATUS_INFO = { enrolled: { color: '#10b981', label: 'Enrolled' }, active: { color: P, label: 'Active' }, available: { color: '#94a3b8', label: 'Available' }, not_eligible: { color: '#e2e8f0', label: 'Not Eligible' } };

export default function BenefitsScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || '1';
  const [benefits, setBenefits] = useState([]);
  const [pension, setPension] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const load = useCallback(async () => {
    try {
      const h = await getH();
      const [benRes, penRes] = await Promise.allSettled([
        fetch(`${API}/api/data/benefits?employee_id=${parseInt(empId)||0}`, { headers: h }),
        fetch(`${API}/api/pension/${parseInt(empId)||0}`, { headers: h }),
      ]);
      let benData = DEFAULT_BENEFITS;
      if (benRes.status === 'fulfilled' && benRes.value.ok) {
        const json = await benRes.value.json().catch(() => ({}));
        if (Array.isArray(json?.data) && json.data.length) benData = json.data;
      }
      if (penRes.status === 'fulfilled' && penRes.value.ok) {
        const json = await penRes.value.json().catch(() => ({}));
        if (json?.data) setPension(json.data);
      }
      setBenefits(benData);
    } catch { setBenefits(DEFAULT_BENEFITS); }
    finally { setLoading(false); setRefreshing(false); }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const categories = ['All', ...new Set(benefits.map(b => b.category || 'Other'))];
  const filtered = activeCategory === 'All' ? benefits : benefits.filter(b => b.category === activeCategory);

  return (
    <SafeAreaView style={sb.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={sb.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={sb.back}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <Text style={sb.headerTitle}>My Benefits</Text>
        <View style={{ width: 36 }} />
      </LinearGradient>

      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
        {pension && (
          <LinearGradient colors={['#1e7be2', P]} style={sb.pensionCard}>
            <Text style={sb.pensionLabel}>Pension (NEST)</Text>
            <Text style={sb.pensionVal}>{((pension.employee_contribution || 0) * 100).toFixed(0)}% + {((pension.employer_contribution || 0) * 100).toFixed(0)}%</Text>
            <Text style={sb.pensionSub}>Employee + Employer contribution</Text>
          </LinearGradient>
        )}

        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={sb.cats} contentContainerStyle={{ paddingHorizontal: 16, gap: 8 }}>
          {categories.map(c => (
            <TouchableOpacity key={c} onPress={() => setActiveCategory(c)} style={[sb.catChip, activeCategory === c && sb.catChipActive]}>
              <Text style={[sb.catChipText, activeCategory === c && sb.catChipTextActive]}>{c}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        <View style={sb.list}>
          {filtered.map((b, i) => {
            const si = STATUS_INFO[b.status] || STATUS_INFO.available;
            return (
              <TouchableOpacity key={b.id || i} style={sb.benefitCard} onPress={() => setSelected(b)}>
                <View style={[sb.benefitIcon, { backgroundColor: (b.color || P) + '15' }]}>
                  <MaterialCommunityIcons name={b.icon || 'gift'} size={26} color={b.color || P} />
                </View>
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={sb.benefitTitle}>{b.title}</Text>
                  <Text style={sb.benefitValue} numberOfLines={1}>{b.value || b.description}</Text>
                </View>
                <View style={[sb.statusBadge, { backgroundColor: si.color + '20' }]}>
                  <Text style={[sb.statusText, { color: si.color }]}>{si.label}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>

      {selected && (
        <View style={sb.detailPanel}>
          <View style={sb.detailHeader}>
            <View style={[sb.detailIcon, { backgroundColor: (selected.color || P) + '20' }]}>
              <MaterialCommunityIcons name={selected.icon || 'gift'} size={28} color={selected.color || P} />
            </View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={sb.detailTitle}>{selected.title}</Text>
              <Text style={[sb.detailValue, { color: selected.color || P }]}>{selected.value}</Text>
            </View>
            <TouchableOpacity onPress={() => setSelected(null)}><MaterialCommunityIcons name="close" size={22} color="#94a3b8" /></TouchableOpacity>
          </View>
          <Text style={sb.detailDesc}>{selected.description}</Text>
          {selected.status === 'available' && (
            <TouchableOpacity style={[sb.enrollBtn, { backgroundColor: selected.color || P }]}
              onPress={() => Alert.alert('Enrol', `Contact HR to enrol in ${selected.title}`)}>
              <Text style={sb.enrollBtnText}>Enquire to Enrol</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </SafeAreaView>
  );
}

const sb = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  pensionCard: { margin: 16, borderRadius: 16, padding: 20 },
  pensionLabel: { color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: '600' },
  pensionVal: { color: '#fff', fontSize: 32, fontWeight: '900', marginVertical: 4 },
  pensionSub: { color: 'rgba(255,255,255,0.8)', fontSize: 12 },
  cats: { marginBottom: 8 },
  catChip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20, borderWidth: 1.5, borderColor: '#e2e8f0', backgroundColor: '#fff' },
  catChipActive: { backgroundColor: P, borderColor: P },
  catChipText: { fontSize: 12, fontWeight: '600', color: '#64748b' },
  catChipTextActive: { color: '#fff' },
  list: { padding: 16, gap: 8 },
  benefitCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 14, padding: 14, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  benefitIcon: { width: 52, height: 52, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  benefitTitle: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  benefitValue: { fontSize: 12, color: '#64748b', marginTop: 2 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 },
  statusText: { fontSize: 10, fontWeight: '700' },
  detailPanel: { backgroundColor: '#fff', margin: 16, borderRadius: 20, padding: 20, shadowColor: '#000', shadowOpacity: 0.12, shadowRadius: 16, elevation: 8 },
  detailHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  detailIcon: { width: 56, height: 56, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  detailTitle: { fontSize: 16, fontWeight: '800', color: '#1e293b' },
  detailValue: { fontSize: 14, fontWeight: '600', marginTop: 2 },
  detailDesc: { fontSize: 14, color: '#64748b', lineHeight: 22, marginBottom: 16 },
  enrollBtn: { padding: 14, borderRadius: 12, alignItems: 'center' },
  enrollBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
});
